import { criterion, fail, fromCriteria, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { isUnderlined, readDocument } from "@/office/word/document";

export const lektionAuszeichnungen: Lesson = {
  id: "auszeichnungen",
  title: "Fett, kursiv und unterstrichen",
  summary: "Einzelne Wörter gezielt hervorheben – ohne den Text zu überladen.",
  durationMinutes: 8,
  learningGoals: [
    "Fett, kursiv und unterstrichen sicher anwenden",
    "Die passenden Tastenkürzel kennen",
    "Auszeichnungen bewusst dosieren",
  ],
  steps: [
    {
      id: "fett-setzen",
      title: "Wichtiges fett hervorheben",
      explanation: [
        "Fett ist die stärkste Auszeichnung im Fließtext. Sie zieht den Blick beim " +
          "Überfliegen sofort an und eignet sich deshalb für Schlüsselbegriffe, Namen " +
          "oder Zahlen, die man wiederfinden können muss.",
        "Genau deshalb funktioniert Fett nur sparsam: Ist ein halber Absatz fett, hebt " +
          "sich nichts mehr ab und der Effekt verpufft.",
      ],
      keyPoints: [
        "Tastenkürzel: Strg + Umschalt + F (in vielen Installationen auch Strg + B).",
        "Fett markiert einzelne Wörter – nicht ganze Absätze.",
      ],
      task: {
        instruction:
          "Hebe in einem deiner Absätze ein einzelnes Wort fett hervor. Der restliche " +
          "Absatz bleibt normal.",
        hint: "Ein Doppelklick auf ein Wort markiert es komplett. Danach das Tastenkürzel drücken.",
        check: async () => {
          const snapshot = await readDocument();
          if (snapshot.runs.length === 0) {
            return fail("Im Dokument steht noch kein Text.", "Schreibe zuerst etwas Text.");
          }

          const boldRuns = snapshot.runs.filter((run) => run.bold === true);
          if (boldRuns.length === 0) {
            return fail(
              "Es ist noch kein Wort fett formatiert.",
              "Wort per Doppelklick markieren und Strg + Umschalt + F drücken.",
            );
          }

          const paragraph = snapshot.filledParagraphs.find(
            (item) => item.index === boldRuns[0].paragraphIndex,
          );
          const wholeParagraphBold =
            paragraph !== undefined &&
            paragraph.runs.length > 1 &&
            paragraph.runs.every((run) => run.bold === true);

          if (wholeParagraphBold) {
            return fail(
              "Aktuell ist der komplette Absatz fett – damit hebt sich kein Wort mehr hervor.",
              "Markiere den Absatz, hebe die Auszeichnung wieder auf und setze anschließend nur ein einzelnes Wort fett.",
            );
          }

          return pass(
            `Sehr gut – "${boldRuns[0].text}" sticht jetzt aus dem Absatz hervor.`,
          );
        },
      },
    },
    {
      id: "kursiv-und-unterstrichen",
      title: "Kursiv und unterstrichen einsetzen",
      explanation: [
        "Kursiv ist die leiseste der drei Auszeichnungen. Sie wird für Werktitel, " +
          "fremdsprachige Begriffe oder eine gedankliche Betonung verwendet und stört " +
          "den Lesefluss kaum.",
        "Unterstrichen ist dagegen ein Sonderfall: Seit es Links im Web gibt, lesen die " +
          "meisten Menschen unterstrichenen Text automatisch als anklickbar. In " +
          "Fließtexten solltest du deshalb lieber fett oder kursiv verwenden – " +
          "Unterstreichungen sind vor allem in Formularen und auf Ausfüllzeilen sinnvoll.",
      ],
      keyPoints: [
        "Kursiv: Strg + Umschalt + K. Unterstrichen: Strg + Umschalt + U.",
        "Unterstrichener Text wird leicht mit einem Link verwechselt.",
      ],
      task: {
        instruction:
          "Formatiere ein Wort kursiv und ein anderes Wort unterstrichen – zwei " +
          "verschiedene Wörter.",
        hint: "Du kannst dafür Wörter aus unterschiedlichen Absätzen nehmen.",
        check: async () => {
          const snapshot = await readDocument();

          const italicRun = snapshot.runs.find((run) => run.italic === true);
          const underlinedRun = snapshot.runs.find(
            (run) => isUnderlined(run) && run.text !== italicRun?.text,
          );

          return fromCriteria(
            [
              criterion("Ein Wort ist kursiv formatiert", Boolean(italicRun)),
              criterion("Ein anderes Wort ist unterstrichen", Boolean(underlinedRun)),
            ],
            {
              success: "Alle drei Auszeichnungen sitzen – gut gemacht.",
              failure: "Es fehlt noch eine der beiden Auszeichnungen.",
              hint: "Achte darauf, dass es zwei unterschiedliche Wörter sind. Ein Wort, das gleichzeitig kursiv und unterstrichen ist, zählt nur einmal.",
            },
          );
        },
      },
    },
    {
      id: "dosierung",
      title: "Auszeichnungen richtig dosieren",
      explanation: [
        "Auszeichnungen funktionieren nach dem Prinzip der Ausnahme: Sie wirken nur, " +
          "solange sie selten sind. Als grobe Orientierung sollten in einem Absatz " +
          "höchstens ein bis zwei Stellen hervorgehoben sein.",
        "Und noch ein Hinweis für später: Kombiniere möglichst nie fett, kursiv und " +
          "unterstrichen gleichzeitig am selben Wort. Das Ergebnis wirkt unruhig und " +
          "verrät, dass die Hervorhebung nicht geplant war.",
      ],
      keyPoints: [
        "Weniger ist mehr: ein bis zwei Hervorhebungen pro Absatz.",
        "Auszeichnungen nicht stapeln – eine pro Stelle genügt.",
        "Strg + Leertaste entfernt alle Zeichenformatierungen der Markierung.",
      ],
    },
  ],
};
